import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Row, Col, Form } from 'react-bootstrap';
import { get } from 'lodash';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import Select from 'react-select';

import Loading from '../../components/Loading';
import Image from '../../components/CustomImage';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';
import Forms from '../../components/Forms';
import ErrorProcessor from '../../components/ErrorProcessor';

// URL base da API (usada para compor o caminho da foto)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Função helper para converter caminhos de foto
const convertPhotoPath = (photoPath, authToken, apiBaseUrl) => {
  if (!photoPath) return '';

  const normalizedPath = String(photoPath).replace(/\\/g, '/');
  let finalUrl = '';

  // Se já for uma URL absoluta
  if (/^https?:\/\//i.test(normalizedPath)) {
    finalUrl = normalizedPath;
  } else {
    // Converte /public/ ou /api/public/ para /download/ (sem /api/ pois apiBaseUrl já tem)
    let convertedPath = normalizedPath;
    if (convertedPath.startsWith('/api/public/')) {
      // Remove /api/public/ e adiciona /download/
      convertedPath = convertedPath.replace('/api/public/', '/download/');
    } else if (convertedPath.startsWith('/api/download/')) {
      // Remove /api/ do início pois apiBaseUrl já tem
      convertedPath = convertedPath.replace('/api/download/', '/download/');
    } else if (convertedPath.startsWith('/public/')) {
      // Remove /public/ e adiciona /download/
      convertedPath = convertedPath.replace('/public/', '/download/');
    } else if (
      !convertedPath.startsWith('/download/') &&
      !convertedPath.startsWith('/')
    ) {
      // Se não começar com /, adiciona /download/
      convertedPath = `/download/${convertedPath}`;
    }

    // Constrói a URL completa (apiBaseUrl já contém /api)
    finalUrl = `${apiBaseUrl}${convertedPath}`;
  }

  // Adiciona o token na query string se disponível
  if (authToken && authToken !== false && finalUrl.includes('/download/')) {
    const separator = finalUrl.includes('?') ? '&' : '?';
    finalUrl = `${finalUrl}${separator}token=${encodeURIComponent(authToken)}`;
  }

  return finalUrl;
};

export default function User() {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const notification = useNotification();
  const navigate = useNavigate();
  const params = useParams();
  const id = get(params, 'id', '');
  const authToken = useSelector((state) => state.auth.token);
  const [isLoading, setIsLoading] = useState(false);
  const [photo, setPhoto] = useState('');
  const [companies, setCompanies] = useState([]);
  const [accesses, setAccesses] = useState([]);

  // Obtemos os dados do usuário logado (exemplo: { id, isadmin, empresa_id, ... })
  const userData = useSelector((state) => state.auth.user);

  // Estado inicial do formulário
  const [formValues, setFormValues] = useState({
    name: '',
    lastname: '',
    email: '',
    ramal: '',
    password: '',
    companyId: '',
    accessIds: [], // Array para múltiplos acessos
  });

  // Esquema de validação do formulário.
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Nome deve conter no mínimo 3 caracteres.')
      .required('Nome obrigatório.'),
    lastname: Yup.string()
      .min(3, 'Sobrenome deve conter no mínimo 3 caracteres.')
      .required('Sobrenome obrigatório.'),
    ramal: Yup.string()
      .min(3, 'Ramal deve conter no mínimo 3 caracteres.')
      .required('Ramal obrigatório.'),
    email: Yup.string()
      .required('E-mail obrigatório.')
      .email('E-mail inválido.'),
    password: !id
      ? Yup.string()
          .min(6, 'Senha deve conter no mínimo 6 caracteres.')
          .required('Senha obrigatória.')
      : Yup.string(),
    confirmPassword: !id
      ? Yup.string()
          .oneOf([Yup.ref('password'), null], 'Senhas devem ser iguais.')
          .required('Confirme sua senha.')
      : Yup.string(),
    // Campo empresa obrigatório na criação
    ...(!id
      ? { companyId: Yup.string().required('Selecione uma empresa.') }
      : {}),
    // Validação para acessos (opcional, mas se selecionado deve ter pelo menos um)
    ...(!id
      ? {
          accessIds: Yup.array().min(
            0,
            'Selecione pelo menos um acesso se desejar atribuir.'
          ),
        }
      : {}),
  });

  // Função para atualizar os valores do formulário
  const handleFieldChange = useCallback((fieldName, value) => {
    setFormValues((prevValues) => ({ ...prevValues, [fieldName]: value }));
  }, []);

  // Configuração dos campos do formulário
  const fields = [
    {
      name: 'name',
      id: 'name',
      label: 'Nome',
      type: 'text',
      onChange: (e) => handleFieldChange('name', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'lastname',
      id: 'lastname',
      label: 'Sobrenome',
      type: 'text',
      onChange: (e) => handleFieldChange('lastname', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'email',
      id: 'email',
      label: 'E-mail',
      type: 'email',
      groupText: '@',
      onChange: (e) => handleFieldChange('email', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
  ];

  // Caso seja criação (sem id), adicionamos os demais campos
  if (!id) {
    fields.push({
      name: 'ramal',
      id: 'ramal',
      label: 'Ramal',
      type: 'text',
      onChange: (e) => handleFieldChange('ramal', e.target.value),
      colSize: 4,
      additionalProps: {},
    });
    fields.push({
      name: 'password',
      id: 'password',
      label: 'Senha',
      type: 'password',
      onChange: (e) => handleFieldChange('password', e.target.value),
      colSize: 4,
      additionalProps: {},
    });
    fields.push({
      name: 'confirmPassword',
      id: 'confirmPassword',
      label: 'Confirme a senha',
      type: 'password',
      onChange: (e) => handleFieldChange('confirmPassword', e.target.value),
      colSize: 4,
      additionalProps: {},
    });

    // Campo para selecionar a empresa (visível para todos os usuários)
    fields.push({
      name: 'companyId',
      id: 'companyId',
      label: 'Empresa',
      type: 'select',
      options: [
        { value: '', label: '-- Selecione --' },
        ...companies.map((company) => ({
          value: company.id,
          label: company.name,
        })),
      ],
      // Lembre que alguns componentes de select podem enviar o valor via e.target.value ou e.value
      onChange: (e) =>
        handleFieldChange('companyId', e.value ? e.value : e.target.value),
      colSize: 6,
      additionalProps: {},
    });

    // Campo customizado para acessos usando react-select
    fields.push({
      name: 'accessIds',
      id: 'accessIds',
      label: 'Acessos',
      type: 'custom',
      colSize: 6,
      customComponent: (
        <Select
          isMulti
          value={accesses
            .filter((access) => formValues.accessIds.includes(access.id))
            .map((access) => ({
              value: access.id,
              label: access.name,
            }))}
          onChange={(selectedOptions) => {
            const selectedIds = selectedOptions
              ? selectedOptions.map((option) => option.value)
              : [];
            handleFieldChange('accessIds', selectedIds);
          }}
          options={accesses.map((access) => ({
            value: access.id,
            label: access.name,
          }))}
          placeholder="Selecione os acessos para o usuário"
          styles={{
            control: (provided) => ({
              ...provided,
              minHeight: '38px',
              borderColor: '#ced4da',
              '&:hover': {
                borderColor: '#ced4da',
              },
            }),
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: '#e9ecef',
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: '#495057',
            }),
          }}
        />
      ),
      helpText: 'Selecione um ou mais acessos que o usuário terá no sistema',
    });
  }

  // Função de envio do formulário
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      let action = 'cadastrado';

      // Verifica se a empresa foi selecionada na criação
      if (!id && !formValues.companyId) {
        setApiErrors({ error: 'Selecione uma empresa antes de continuar.' });
        setIsLoading(false);
        return;
      }

      if (id) {
        // Atualiza o e-mail do usuário
        const userPayload = { email: formValues.email };
        await axios.put(`/users/${id}`, userPayload);

        // Atualiza os dados de perfil (nome e sobrenome)
        const profilePayload = {
          name: formValues.name,
          lastname: formValues.lastname,
        };
        await axios.put(`/profiles/${id}`, profilePayload);

        action = 'atualizado';
      } else {
        // Mapeia os dados do formulário para o payload esperado pela API na criação
        const payload = {
          ...formValues,
          empresa_id: formValues.companyId,
        };
        // O POST /users já cria, na mesma transação, o perfil (nome e
        // sobrenome) e o vínculo com a empresa enviada em companyId.
        // Repetir essas chamadas aqui gerava um perfil duplicado e esbarrava
        // na validação de vínculo já existente.
        const getUser = await axios.post(`/users/`, payload);

        // Se foram selecionados acessos, criar as associações
        if (formValues.accessIds && formValues.accessIds.length > 0) {
          const accessPromises = formValues.accessIds.map((accessId) =>
            axios.post('/accessesusers', {
              user_id: getUser.data.id,
              access_id: accessId,
            })
          );
          await Promise.all(accessPromises);
        }
      }
      notification(`Usuário ${action} com sucesso`, 'success');
      navigate('/users');
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Busca as empresas para o select (disponível para todos os usuários)
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const { data } = await axios.get('/companies');
        setCompanies(data);
      } catch (err) {
        setApiErrors(err);
      }
    }
    if (!id) {
      fetchCompanies();
    }
  }, [id]);

  // Busca os acessos disponíveis para seleção durante a criação
  useEffect(() => {
    async function fetchAccesses() {
      try {
        const { data } = await axios.get('/accesses');
        setAccesses(data);
      } catch (err) {
        setApiErrors(err);
      }
    }
    if (!id) {
      fetchAccesses();
    }
  }, [id]);

  // Quando for atualização, carrega os dados do usuário
  useEffect(() => {
    if (!id) return;
    async function getData() {
      try {
        setIsLoading(true);
        const { data } = await axios.get(`users/${id}`);

        // Concatena o caminho da API com o caminho relativo da foto
        const photoPath = get(data, 'UserProfile.photo', '');
        const photoUrl = convertPhotoPath(photoPath, authToken, API_BASE_URL);
        setPhoto(photoUrl);

        setFormValues({
          ...data,
          name:
            data.UserProfile && data.UserProfile.name
              ? data.UserProfile.name
              : '',
          lastname:
            data.UserProfile && data.UserProfile.lastname
              ? data.UserProfile.lastname
              : '',
          ramal:
            data.UserProfile && data.UserProfile.ramal
              ? data.UserProfile.ramal
              : data.ramal,
          email: data.email,
          companyId: data.empresa_id, // Empresa já associada ao usuário
        });
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
        setDataLoaded(true);
      }
    }
    getData();
  }, [id, notification, navigate]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs
        title={id ? 'Atualizar' : 'Cadastrar'}
        pages={[{ link: '/users', name: 'Usuários' }]}
      />
      <section>
        <Row>
          {id && (
            <Col lg={4}>
              <Card className="card-profile mb-4">
                <Card.Header
                  className="overflow-hidden"
                  style={{
                    backgroundImage: `url('/img/photos/greenShapes.jpg')`,
                  }}
                />
                <Card.Body className="text-center">
                  <div
                    className="card-profile-img mx-auto d-flex align-center"
                    style={{ maxHeight: '8rem' }}
                  >
                    <div className="position-relative overflow-hidden rounded-circle">
                      <Link
                        to={`/profile/${id}`}
                        title="Clique para alterar a foto"
                      >
                        <Image
                          src={photo || '/img/user-profile-circle.png'}
                          layout="fixed"
                          width={122}
                          height={122}
                          priority
                          alt={formValues.name}
                        />
                      </Link>
                    </div>
                  </div>
                  <h3 className="mb-3">{`${formValues.name} ${formValues.lastname}`}</h3>
                  <p className="mb-4">{formValues.email}</p>
                </Card.Body>
              </Card>
            </Col>
          )}
          <Col lg={id ? 8 : 12}>
            <Card className="mb-5">
              <Card.Header>
                <h4 className="card-heading">
                  {id ? 'Atualizar usuário' : 'Cadastrar usuário'}
                </h4>
              </Card.Header>
              <Card.Body>
                <ErrorProcessor error={apiErrors} />
                {(!id || dataLoaded) && (
                  <Forms
                    initialValues={formValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    fields={fields}
                    btnSubmit={{
                      variant: 'primary',
                      type: 'submit',
                      text: id ? 'Atualizar' : 'Cadastrar',
                    }}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </section>
    </Container>
  );
}
